import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { WorkflowPreview } from '@/components/chat/WorkflowPreview';

/**
 * Attempts to normalize various workflow JSON formats into a shape WorkflowPreview can render.
 * Returns the normalized object or null if the data doesn't look like a workflow.
 */
function normalizeWorkflowData(data: any): any | null {
  // Already in the expected format (has definition with nodes/edges)
  if (data.definition?.nodes || data.trigger_config) {
    return data;
  }

  // Format: {"workflow": {"steps": [...]}} or {"workflow": {"nodes": [...]}}
  if (data.workflow) {
    const wf = data.workflow;
    if (wf.steps) {
      return stepsToWorkflowPreview(wf.steps, data.name);
    }
    if (wf.nodes) {
      return { definition: { nodes: wf.nodes, edges: wf.edges || [] }, name: data.name };
    }
  }

  // Format: {"steps": [...]}
  if (data.steps && Array.isArray(data.steps)) {
    return stepsToWorkflowPreview(data.steps, data.name);
  }

  // Format: {"nodes": [...], "edges": [...]} at top level
  if (data.nodes && Array.isArray(data.nodes)) {
    return { definition: { nodes: data.nodes, edges: data.edges || [] }, name: data.name };
  }

  // Format: {"actions": [...]} (legacy simplified)
  if (data.actions && Array.isArray(data.actions)) {
    return data;
  }

  return null;
}

/**
 * Converts a flat steps array (agent format) into WorkflowPreview's actions format.
 * Each step has: {name, action, parameters?}
 */
function stepsToWorkflowPreview(steps: any[], name?: string): any {
  const trigger = steps.find((s: any) => s.action?.startsWith('trigger-') || s.name?.startsWith('trigger'));
  const actionSteps = steps.filter((s: any) => !s.action?.startsWith('trigger-') && !s.name?.startsWith('trigger'));

  const triggerObj = trigger ? {
    source: trigger.action?.includes('pod') || trigger.action?.includes('kubernetes') ? 'kubernetes' :
            trigger.action?.includes('github') ? 'github' :
            trigger.action?.includes('pagerduty') ? 'pagerduty' :
            trigger.action?.includes('posthog') ? 'posthog' :
            trigger.action?.includes('aws') || trigger.action?.includes('cloud') ? 'aws' :
            'kubernetes',
    signal_type: trigger.action?.replace('trigger-', '') || 'event',
    description: formatStepLabel(trigger.action?.replace('trigger-', '') || trigger.name || 'Trigger'),
  } : undefined;

  const actions = actionSteps.map((s: any) => ({
    action_id: s.action || s.name || 'unknown',
    label: formatStepLabel(s.action || s.name || 'Step'),
    integration: mapStepToIntegration(s.action || ''),
  }));

  return { name, trigger: triggerObj, actions };
}

function formatStepLabel(action: string): string {
  const brandMap: Record<string, string> = {
    argocd: 'ArgoCD', rollouts: 'Rollouts', flux: 'Flux', github: 'GitHub', gitlab: 'GitLab', pagerduty: 'PagerDuty',
    posthog: 'PostHog', datadog: 'Datadog', jira: 'Jira', linear: 'Linear', slack: 'Slack',
    kubernetes: 'Kubernetes', k8s: 'K8s', aws: 'AWS', confluence: 'Confluence',
    vercel: 'Vercel', railway: 'Railway', flyio: 'Fly.io', nebius: 'Nebius', beam: 'Beam', daytona: 'Daytona', supabase: 'Supabase', planetscale: 'PlanetScale', neon: 'Neon', clickhouse: 'ClickHouse', jenkins: 'Jenkins', circleci: 'CircleCI', sonarcloud: 'SonarCloud', okta: 'Okta', databricks: 'Databricks', kyverno: 'Kyverno', trivy: 'Trivy', terraform: 'Terraform', pulumi: 'Pulumi', vault: 'Vault', infisical: 'Infisical', dd: 'Datadog', pd: 'PagerDuty',
    gcp: 'GCP', gke: 'GKE', bigquery: 'BigQuery', scc: 'SCC', iam: 'IAM', sql: 'SQL', mig: 'MIG', cud: 'CUD', cloudrun: 'Cloud Run', pubsub: 'Pub/Sub', ips: 'IPs', ip: 'IP',
  };
  const parts = action.split('-');
  // Replace known brand prefixes with proper casing
  const formatted = parts.map(w => brandMap[w.toLowerCase()] || w.charAt(0).toUpperCase() + w.slice(1));
  return formatted.join(' ');
}

function mapStepToIntegration(action: string): string {
  if (action.startsWith('slack')) return 'slack';
  if (action.startsWith('jira')) return 'jira';
  if (action.startsWith('linear')) return 'linear';
  if (action.startsWith('github')) return 'github';
  if (action.startsWith('gitlab')) return 'gitlab';
  if (action.startsWith('rollouts')) return 'argo-rollouts';
  if (action.startsWith('flux')) return 'fluxcd';
  if (action.startsWith('karpenter')) return 'karpenter';
  if (action.startsWith('kyverno')) return 'kyverno';
  if (action.startsWith('trivy')) return 'trivy';
  if (action.startsWith('argocd')) return 'argocd';
  if (action.startsWith('pagerduty') || action.startsWith('pd-')) return 'pagerduty';
  if (action.startsWith('posthog')) return 'posthog';
  if (action.startsWith('datadog') || action.startsWith('dd-')) return 'datadog';
  if (action.startsWith('aws') || action.startsWith('cloud-cost')) return 'aws-cost';
  if (action.startsWith('approval')) return 'approval';
  if (action.startsWith('confluence')) return 'confluence';
  if (action.startsWith('vercel')) return 'vercel';
  if (action.startsWith('railway')) return 'railway';
  if (action.startsWith('flyio')) return 'flyio';
  if (action.startsWith('nebius')) return 'nebius';
  if (action.startsWith('beam')) return 'beam';
  if (action.startsWith('daytona')) return 'daytona';
  if (action.startsWith('supabase')) return 'supabase';
  if (action.startsWith('planetscale')) return 'planetscale';
  if (action.startsWith('neon')) return 'neon';
  if (action.startsWith('clickhouse')) return 'clickhouse';
  if (action.startsWith('jenkins')) return 'jenkins';
  if (action.startsWith('circleci')) return 'circleci';
  if (action.startsWith('sonarcloud')) return 'sonarcloud';
  if (action.startsWith('okta')) return 'okta';
  if (action.startsWith('databricks')) return 'databricks';
  if (action.startsWith('gcp')) return 'gcp';
  if (action.startsWith('terraform')) return 'terraform';
  if (action.startsWith('pulumi')) return 'pulumi';
  if (action.startsWith('vault')) return 'vault';
  if (action.startsWith('infisical')) return 'infisical';
  if (action.startsWith('kubernetes') || action.startsWith('k8s') || action.startsWith('kubectl')) return 'kubernetes';
  if (action.startsWith('condition') || action.startsWith('check-')) return 'kestrel';
  return 'kestrel';
}

interface ChatMarkdownProps {
  text: string;
  onWorkflowSave?: (workflow: any) => Promise<boolean | void> | boolean | void;
  onWorkflowCreate?: (workflow: any) => void;
  onWorkflowEdit?: (workflow: any) => void;
  /** Render completed workflow blocks as a compact "applied to canvas" chip
   *  instead of the full graph card (builder mode — the canvas already shows
   *  the workflow, so the card would be redundant). */
  compactWorkflowBlocks?: boolean;
}

export function ChatMarkdown({ text, onWorkflowSave, onWorkflowCreate, onWorkflowEdit, compactWorkflowBlocks }: ChatMarkdownProps) {
  // Detect streaming workflow/fix block: text contains ```workflow or ```fix but no closing ```
  const workflowBlockStart = text.indexOf('```workflow');
  const fixBlockStart = text.indexOf('```fix');
  const blockStart = workflowBlockStart !== -1 ? workflowBlockStart : fixBlockStart;
  const blockTagLen = workflowBlockStart !== -1 ? 11 : 6;
  const hasIncompleteWorkflow = blockStart !== -1 && text.indexOf('```', blockStart + blockTagLen) === -1;

  // Split text: render everything before the workflow block normally, show shimmer for the incomplete block
  const textBeforeWorkflow = hasIncompleteWorkflow ? text.slice(0, blockStart).replace(/`{1,3}$/, '') : text;

  return (
    <div className="w-full">
    {textBeforeWorkflow.trim() && (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        code: ({ className, children, ...props }) => {
          const isBlock = className?.startsWith('language-');
          if (isBlock) {
            const language = className?.replace('language-', '') || 'code';
            const codeStr = String(children).replace(/\n$/, '');

            // Only render as a styled code block if the language is recognized
            const knownLanguages = ['workflow', 'fix', 'json', 'yaml', 'python', 'javascript', 'typescript', 'go', 'bash', 'shell', 'sql', 'html', 'css', 'xml', 'markdown', 'diff', 'dockerfile', 'hcl', 'terraform', 'ruby', 'java', 'rust', 'c', 'cpp', 'code'];
            if (!knownLanguages.includes(language.toLowerCase())) {
              return <p className="mb-2 last:mb-0">{children}</p>;
            }

            // Render workflow blocks as a visual mini-graph
            if (language === 'workflow') {
              try {
                // Try to find JSON object in the code string (agent sometimes includes extra text)
                let jsonStr = codeStr;
                const jsonStart = codeStr.indexOf('{');
                const jsonEnd = codeStr.lastIndexOf('}');
                console.log('[WorkflowRender] codeStr length:', codeStr.length, 'first 100 chars:', codeStr.slice(0, 100), 'last 50 chars:', codeStr.slice(-50));
                if (jsonStart >= 0 && jsonEnd > jsonStart) {
                  jsonStr = codeStr.slice(jsonStart, jsonEnd + 1);
                  console.log('[WorkflowRender] Extracted JSON from position', jsonStart, 'to', jsonEnd + 1, 'length:', jsonStr.length);
                }
                const wf = JSON.parse(jsonStr);
                console.log('[WorkflowRender] JSON parsed successfully, keys:', Object.keys(wf));
                const normalized = normalizeWorkflowData(wf) || wf;
                if (compactWorkflowBlocks) {
                  // Builder mode: the canvas already renders the workflow, so
                  // a graph card here would be redundant — show a compact
                  // confirmation chip instead.
                  return (
                    <div className="my-3 flex items-center gap-2.5 rounded-lg border border-border bg-muted/30 px-3 py-2">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-emerald-500/10">
                        <svg className="h-3.5 w-3.5 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      </span>
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-foreground truncate">{wf.name || normalized?.name || 'Workflow'}</div>
                        <div className="text-[11px] text-muted-foreground">Applied to the canvas</div>
                      </div>
                    </div>
                  );
                }
                return (
                  <WorkflowPreview
                    workflow={normalized}
                    onSave={onWorkflowSave}
                    onCreate={onWorkflowCreate}
                    onEdit={onWorkflowEdit}
                  />
                );
              } catch (e) {
                // Completed block with invalid JSON — show error state
                console.error('[WorkflowRender] Failed to parse workflow JSON:', e, 'codeStr first 200:', codeStr.slice(0, 200));
                return (
                  <div className="my-3 rounded-lg border border-border overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-6 justify-center">
                      <p className="text-sm text-muted-foreground">Failed to parse workflow</p>
                    </div>
                  </div>
                );
              }
            }

            // Render fix blocks - try as workflow diagram first, then as action button
            if (language === 'fix') {
              try {
                const fixData = JSON.parse(codeStr);
                // Normalize various workflow JSON formats into WorkflowPreview-compatible shape
                const normalized = normalizeWorkflowData(fixData);
                if (normalized) {
                  return (
                    <WorkflowPreview
                      workflow={normalized}
                      onEdit={onWorkflowEdit}
                    />
                  );
                }
                const workflowId = fixData.workflow_id;
                if (workflowId) {
                  return (
                    <div className="my-3 rounded-lg border border-border overflow-hidden p-3">
                      <p className="text-xs text-muted-foreground mb-2">{fixData.description || 'Suggested fix available'}</p>
                      <a
                        href={`/workflows/${workflowId}/edit`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                      >
                        Fix in Builder
                      </a>
                    </div>
                  );
                }
              } catch {
                // Ignore invalid fix blocks
              }
            }

            return (
              <div className="my-2 rounded-md overflow-hidden border border-border/50">
                <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b border-border/50">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase">{language}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(String(children).replace(/\n$/, ''))}
                    className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Copy
                  </button>
                </div>
                <pre className="p-3 overflow-x-auto bg-muted/30">
                  <code className="text-xs font-mono">{children}</code>
                </pre>
              </div>
            );
          }
          return (
            <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono" {...props}>
              {children}
            </code>
          );
        },
        pre: ({ children }) => {
          // Fenced code blocks without a language label render as <pre><code>
          // without a className. Detect this case and render as a block.
          const child = children as React.ReactElement<{ className?: string; children?: React.ReactNode }>;
          if (child?.type === 'code' && !child?.props?.className) {
            const codeContent = String(child.props.children || '').replace(/\n$/, '');
            return (
              <div className="my-2 rounded-md overflow-hidden border border-border/50">
                <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b border-border/50">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase">code</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(codeContent)}
                    className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Copy
                  </button>
                </div>
                <pre className="p-3 overflow-x-auto bg-muted/30">
                  <code className="text-xs font-mono">{child.props.children}</code>
                </pre>
              </div>
            );
          }
          // For labeled code blocks, the code component already handles rendering
          return <>{children}</>;
        },
        ul: ({ children }) => <ul className="mb-2 ml-4 list-disc space-y-1">{children}</ul>,
        ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal space-y-1">{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        h1: ({ children }) => <h1 className="text-lg font-bold mb-2 mt-3">{children}</h1>,
        h2: ({ children }) => <h2 className="text-base font-bold mb-2 mt-3">{children}</h2>,
        h3: ({ children }) => <h3 className="text-sm font-bold mb-1 mt-2">{children}</h3>,
        h4: ({ children }) => <h4 className="text-sm font-semibold mb-1 mt-2">{children}</h4>,
        hr: () => <hr className="my-3 border-border/50" />,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div className="my-2 overflow-x-auto">
            <table className="min-w-full text-xs border-collapse border border-border/50">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
        th: ({ children }) => <th className="px-2 py-1 text-left font-semibold border border-border/50">{children}</th>,
        td: ({ children }) => <td className="px-2 py-1 border border-border/50">{children}</td>,
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-primary/30 pl-3 my-2 text-muted-foreground italic">{children}</blockquote>
        ),
      }}
    >
      {textBeforeWorkflow}
    </ReactMarkdown>
    )}
    {hasIncompleteWorkflow && (
      <div className="my-3 rounded-lg border border-border overflow-hidden w-full">
        <div className="flex items-center justify-center px-4 py-8">
          <p className="text-sm font-medium shimmer-text">Generating workflow</p>
        </div>
      </div>
    )}
    </div>
  );
}
